import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Globe, Phone } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { CATEGORIES } from "../data/products";

const LOGO =
  "https://customer-assets.emergentagent.com/job_246bdc4f-04a8-499f-a35e-a2d34f82e829/artifacts/etd6qiby_46448324-8a4b-431b-a4f6-49b603bebb50.png";

export default function Footer() {
  const { t, lang } = useI18n();

  return (
    <footer
      className="relative border-t border-white/5 bg-[#050505]"
      data-testid="site-footer"
    >
      <div className="divider-neon" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <Link to="/" className="inline-flex items-center gap-3">
            <img
              src={LOGO}
              alt="Signapharma France"
              className="h-14 w-auto"
            />
          </Link>
          <p className="mt-4 text-sm text-zinc-400 font-body leading-relaxed max-w-xs">
            {t.footer.tagline}
          </p>
          <p className="mt-3 text-[11px] text-zinc-500 font-mono uppercase tracking-widest">
            Marque de LOSTA TECH SAS
          </p>
        </div>

        <div>
          <div className="label-tech mb-4">{t.footer.nav_title}</div>
          <ul className="space-y-2 font-body text-sm">
            <li>
              <Link to="/" className="text-zinc-300 hover:text-[#39FF14] transition-colors">
                {t.nav.home}
              </Link>
            </li>
            <li>
              <Link to="/produits" className="text-zinc-300 hover:text-[#39FF14] transition-colors">
                {t.nav.products}
              </Link>
            </li>
            <li>
              <Link to="/galerie" className="text-zinc-300 hover:text-[#39FF14] transition-colors">
                {t.nav.gallery}
              </Link>
            </li>
            <li>
              <Link to="/enseigne-pharmacie-france" className="text-zinc-300 hover:text-[#39FF14] transition-colors">
                Enseignes pharmacie France
              </Link>
            </li>
            <li>
              <Link to="/pharmacie/lyon" className="text-zinc-400 hover:text-[#39FF14] transition-colors text-xs">
                → Croix pharmacie Lyon
              </Link>
            </li>
            <li>
              <Link to="/pharmacie/annecy" className="text-zinc-400 hover:text-[#39FF14] transition-colors text-xs">
                → Croix pharmacie Annecy
              </Link>
            </li>
            <li>
              <Link to="/pharmacie/bourg-en-bresse" className="text-zinc-400 hover:text-[#39FF14] transition-colors text-xs">
                → Croix pharmacie Bourg-en-Bresse
              </Link>
            </li>
            <li>
              <Link to="/pharmacie/chambery" className="text-zinc-400 hover:text-[#39FF14] transition-colors text-xs">
                → Croix pharmacie Chambéry
              </Link>
            </li>
            <li>
              <Link to="/pharmacie/aix-les-bains" className="text-zinc-400 hover:text-[#39FF14] transition-colors text-xs">
                → Croix pharmacie Aix-les-Bains
              </Link>
            </li>
            <li>
              <Link to="/faq" className="text-zinc-300 hover:text-[#39FF14] transition-colors">
                {t.nav.faq}
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-zinc-300 hover:text-[#39FF14] transition-colors">
                {t.nav.contact}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="label-tech mb-4">{t.footer.cat_title}</div>
          <ul className="space-y-2 font-body text-sm">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/produits/${c.slug}`}
                  className="text-zinc-300 hover:text-[#39FF14] transition-colors"
                >
                  {t.cat[c.key].name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="label-tech mb-4">{t.footer.contact_title}</div>
          <ul className="space-y-3 font-body text-sm text-zinc-300">
            <li className="flex items-start gap-2">
              <MapPin size={16} className="text-[#39FF14] mt-0.5 shrink-0" />
              <span>1103 Grand Route<br/>01630 Saint-Jean-de-Gonville, France</span>
            </li>
            <li className="flex items-start gap-2">
              <Phone size={16} className="text-[#39FF14] mt-0.5 shrink-0" />
              <a
                href="tel:+33610443864"
                className="hover:text-[#39FF14] transition-colors font-medium"
                data-testid="footer-phone-link"
              >
                +33 6 10 44 38 64
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={16} className="text-[#39FF14] mt-0.5 shrink-0" />
              <a
                href="mailto:info@signapharma.fr"
                className="hover:text-[#39FF14] transition-colors"
              >
                info@signapharma.fr
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Globe size={16} className="text-[#39FF14] mt-0.5 shrink-0" />
              <span>www.signapharma.fr</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/5 py-6">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 text-center md:text-left">
            © {new Date().getFullYear()} Signapharma France · LOSTA TECH SAS · SIREN 103 764 270 · {t.footer.rights}
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/cgv"
              data-testid="footer-cgv-link"
              className="font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] transition-colors"
            >
              {lang === "fr" ? "CGV" : "Terms"}
            </Link>
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-600">
              France · {lang.toUpperCase()}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
